/* eslint-disable object-curly-newline */
import queryString from 'query-string';
import { trackToBugsnagApiError } from '@common/config/app_logger';
import { analyticsService } from '@common/analytics';
import {
  loadAvailablePaymentSourcesApi, addPaymentSourceApi, editPaymentSourceApi,
  removePaymentSourceApi
} from '@common/api/api';
import { doneChangingPaymentSource, checkIfCanMakeApplePayPayments } from './purchase_details';
import Const from '../config/const';
import {
  fromMobileApp, clickSourceOrMobileClickSource, paramOrNul,
  noPaymentMethodsEventData, paymentKindToAnalyticsName, currentPaymentMethodData, toCamelCase,
  getPurchaseScreenForPaymentSourcesEvent
} from '../../common/config/utils';
import { aditionalPurchaseProps } from './analytics';
import { refreshReducersFromResponse } from '../config/api_functions';

export const startEditingPaymentSource = (id) => ({
  type: Const.paymentSources.edit,
  id
});

export const managePaymentSource = (id) => ({
  type: Const.paymentSources.manage,
  id
});

const applePayPaymentSources = ['apcko_pg', 'apcko_ka', 'apcko_ps'];
const appleMerchantPaymentSources = ['amtck_ka', 'amtck_pg', 'amtck_ps'];
const googlePayPaymentSources = ['gpcko_ka', 'gpcko_pg', 'gpcko_ps'];

export const doneManagingPaymentSource = () => managePaymentSource(null);
// export const doneEditingPaymentSource = () => startEditingPaymentSource(null);
export const doneAddCard = () => ({  type: Const.paymentSources.doneAddCard });

export const loadAvailable = (forceReload = false) => (dispatch, getState) => {
  if (!forceReload) {
    const { existing } = getState().paymentSources;
    if (existing) {
      // at least not null
      return;
    }
  }
  dispatch({ type: Const.paymentSources.load });
  loadAvailablePaymentSourcesApi().then((sources) => {
    checkIfCanMakeApplePayPayments().then(() => {
      dispatch({ type: Const.paymentSources.loaded, ...sources });
    });
  }).catch((error) => {
    dispatch({ type: Const.paymentSources.done });
    trackToBugsnagApiError('Error loadAvailablePaymentSourcesApi', error);
  });
};

export const trackPaymentMethodRemovedEvent = () => {
  const { paymentSources } = window.store.getState();
  const { current, managingSource, existing } = paymentSources;
  const paymentMethodRemovedParams = {
    'deleted payment method': paymentKindToAnalyticsName(managingSource.kind),
    'default payment method': paymentKindToAnalyticsName(current.kind),
    'num of payment methods': existing.length,
    'default payment method removed': managingSource.isDefault,
    ...aditionalPurchaseProps()
  };
  analyticsService.trackEvent('payment method removed', paymentMethodRemovedParams);
};

export const removeSource = (id) => (dispatch) => {
  dispatch({ type: Const.paymentSources.load });
  removePaymentSourceApi(id).then((sources) => {
    trackPaymentMethodRemovedEvent();
    dispatch({ type: Const.paymentSources.loaded, ...sources });
  }).catch(() => {
    dispatch({ type: Const.paymentSources.done });
    // alert('Cannot load payment sources');
  });
};

export const trackStartAddingCreditCard = (clickSource, kind = 'cc') => {
  const { paymentOptions: { options } } = window.store.getState();
  if (kind === 'cc') {
    analyticsService.trackPageVisit('add new card draw');
  }
  const properties = {
    ...noPaymentMethodsEventData(options),
    ...currentPaymentMethodData(),
    'purchase screen' : clickSourceOrMobileClickSource(clickSource),
    ...aditionalPurchaseProps()
  };
  const eventName = kind === 'cc' ? 'add credit card started' : 'add apple pay started';
  analyticsService.trackEvent(eventName, properties);
};

export const trackCreditCardAdded = ({ params, clickSource, conversionEventId, kind = 'cc' }) => {
  const { paymentOptions: { options } } = window.store.getState();
  const eventName = kind === 'cc' ? 'credit card added' : 'apple pay added';
  const eventProperties = {
    'purchase screen' : clickSourceOrMobileClickSource(clickSource),
    ...params,
    ...currentPaymentMethodData(),
    ...noPaymentMethodsEventData(options),
    ...aditionalPurchaseProps()
  };
  analyticsService.trackEvent(eventName, eventProperties, { conversionEventId, isCustomEvent: true });
};

export const trackAddCreditCardFailed = (params, kind = 'cc') => {
  const {
    scheme, failedReason, errorCode, error, errorName
  } = params;
  const properties = {
    'cc type': scheme ? (scheme).toLowerCase() : undefined,
    ...paramOrNul('failed reason error code', errorCode),
    ...paramOrNul('error message', error),
    ...paramOrNul('failed reason description', errorName),
    ...paramOrNul('failed reason', failedReason),
    ...aditionalPurchaseProps()
  };
  const eventName = kind === 'cc' ? 'add credit card failed' : 'add apple pay failed';
  analyticsService.trackEvent(eventName, properties);
};

export const trackPaymentMethodChanged = (properties, clickSource) => {
  let purchaseScreen = clickSourceOrMobileClickSource(clickSource);
  if (window.location.pathname.includes('/payment_source')) purchaseScreen = 'payment methods';
  const params = {
    ...properties,
    ...currentPaymentMethodData(),
    'purchase screen' : purchaseScreen,
    ...aditionalPurchaseProps()
  };
  analyticsService.trackEvent('payment method changed', params);
};

export const addSource = (kind) => (dispatch) => {
  dispatch({ type: Const.paymentSources.load });
  addPaymentSourceApi(kind)
    .then(({ source: { id, gatewayName }, options }) => {
      dispatch(loadAvailable(true));
      if ([...applePayPaymentSources, ...appleMerchantPaymentSources, 'pp', ...googlePayPaymentSources].find((ps) => gatewayName === ps)) {
        dispatch({ type: Const.paymentOptions.loaded, options, skipNextLoad: true });
        dispatch(doneChangingPaymentSource());
        return;
      }
      dispatch(startEditingPaymentSource(id));
    })
    .catch(() => {
      dispatch({ type: Const.paymentSources.done });
      // alert('Cannot create a payment source');
    });
};

export const clearAddingCardError = () => ({
  type: Const.paymentSources.clearAddingCardError
});

const setAddingCardError = (error) => (dispatch) => {
  dispatch({
    type: Const.paymentSources.addingCardError,
    error
  });
};

export const editCardIfSecurityReasons = (id, kind) => (dispatch) => {
  const params = { generate_tokens: true };
  editPaymentSourceApi({ id, params, type: 'editPaymentSourceApi' }).then(() => {
    dispatch(addSource(kind));
  });
};

export const editPaymentSource = (id) => (dispatch) => {
  // dispatch({ type: Const.editPaymentSources.load, id });
  // dispatch({ type: Const.paymentSources.load });
  const params = { generate_tokens: true };
  editPaymentSourceApi({ id, params, type: 'editPaymentSourceApi' }).then((config) => {
    dispatch({ type: Const.paymentSources.addingCardData, config });
  }).catch(() => {
    dispatch({ type: Const.paymentSources.addingCardData });
  });
};

export const updatePaymentSource = (paymentSourceParams) => (dispatch) => {
  const { id, context, callback } = paymentSourceParams;
  const params = { context };
  editPaymentSourceApi({ id, params, type: 'updatePaymentSourceApi' })
    .then((response) => {
      if (callback) {
        callback(response?.conversionEventId);
      }
      dispatch(doneAddCard());
      dispatch(loadAvailable(true));
      dispatch(doneChangingPaymentSource());
    })
    .catch((error) => {
      const  { cardData: { scheme } } = context;
      try {
        error.json().then(errorJson => {
          dispatch(setAddingCardError(errorJson.error || errorJson.error_name || error.statusText));
          const { errorCode, errorName, error: errorMessage } = errorJson;
          const addCreditCardFailedParams = { scheme, failedReason: 'api error', errorCode, error: errorMessage, errorName };

          refreshReducersFromResponse(toCamelCase(errorJson)); // Need for update user

          trackAddCreditCardFailed(addCreditCardFailedParams);
        });
      } catch {
        const addCreditCardFailedParams = { scheme, failedReason: 'api error', errorCode: error.status, error: error.statusText };
        trackAddCreditCardFailed(addCreditCardFailedParams);
        dispatch(setAddingCardError(error.statusText));
      }
    });
};

const doneMakeDefault = (history, location) => (dispatch) => {
  if (fromMobileApp) {
    const search = queryString.stringify({ state: 'completed', ...(queryString.parse(location.search)) });
    history(`${ location.pathname }?${ search }`, {
      state: { ...location.state },
      replace: true
    });
  }
  dispatch(doneChangingPaymentSource());
};

export const onMakeDefault = (history, location, makeDefParams) => (dispatch, getState) => {
  const {
    kind, type, clickSource, id
  } = makeDefParams;
  const { paymentSources: { current } } = getState();
  if (current && id === current.id) {
    dispatch(doneMakeDefault(history, location));
    return;
  }
  dispatch({ type: Const.paymentSources.load });
  const params = { set_default: true };
  editPaymentSourceApi({ id, params, type: 'makeDefaultSourceApi' })
    .then(({ sources, options }) => {
      const ccType = kind === 'cc' ? { 'cc type': type } : null;
      trackPaymentMethodChanged({
        'payment method' : paymentKindToAnalyticsName(kind),
        'payment id': id,
        ...ccType
      }, clickSource);
      dispatch({ type: Const.paymentSources.loaded, options, ...sources });
      dispatch(doneMakeDefault(history, location));
    }).catch(() => { dispatch({ type: Const.paymentSources.done }); });
};

export const cleanUp = () => (dispatch) => {
  dispatch(doneAddCard());
  dispatch(doneManagingPaymentSource());
};

export const trackPaymentMethodClickedEvent = (kind) => {
  const properties = {
    'chosen payment method': paymentKindToAnalyticsName(kind)
  };
  analyticsService.trackEvent('payment method clicked', properties);
};

export const trackPaymentSourcesEvent = (clickSource) => {
  const { paymentSources } = window.store.getState();
  if (!paymentSources) return;
  let paymentKind = 'none';
  if (paymentSources.current) {
    const { kind } = paymentSources.current;
    paymentKind = kind;
  }
  const numberOfCC = (paymentSources.existing || []).filter(
    source => !source.errorText && source.kind === 'cc'
  ).length;
  const properties = {
    'purchase screen': getPurchaseScreenForPaymentSourcesEvent(clickSource),
    'default payment method': paymentKindToAnalyticsName(paymentKind),
    'number of credit cards': numberOfCC,
    ...aditionalPurchaseProps()
  };
  analyticsService.trackEvent('payment sources screen', properties);
};
